import apiClient from './client'
import type { PaginatedResponse } from '@/types'

// Types
export interface CourseCategory {
  id: number
  name: string
  slug: string
  description: string
  icon: string
  courses_count: number
}

export interface Course {
  id: number
  title: string
  slug: string
  description?: string
  short_description?: string
  thumbnail?: string | null
  thumbnail_url?: string | null
  category: CourseCategory | null
  // Backend returns 'authors' (array), frontend may expect 'instructor' (single)
  authors?: {
    id: number
    display_name: string
    avatar_url: string | null
  }[]
  instructor?: {
    id: number
    full_name: string
    avatar: string | null
  } | null
  level: 'basic' | 'beginner' | 'intermediate' | 'advanced'
  duration_hours: number
  modules_count: number
  lessons_count: number
  enrolled_count: number
  rating?: number
  is_premium?: boolean
  is_published?: boolean
  is_public?: boolean
  is_free?: boolean
  is_enrolled?: boolean
  has_certificate?: boolean
  user_progress?: {
    progress: number
    started_at: string | null
    completed_at: string | null
    certificate: { id: string; certificate_number: string; has_pdf: boolean } | null
    missing_profile_data?: string[]
  } | null
  // Ogni modulo si sblocca al completamento del precedente
  sequential_modules?: boolean
  progress?: number
  created_at?: string
}

export interface BlockingModule {
  id: number
  title: string
}

export interface Module {
  id: number
  course_id: number
  title: string
  description: string
  order: number
  lessons_count: number
  duration_minutes: number
  lessons: Lesson[]
  is_locked?: boolean
  blocking_module?: BlockingModule | null
}

export interface Lesson {
  id: number
  module_id: number
  title: string
  description: string
  type: 'video' | 'text' | 'quiz' | 'scorm' | 'pdf' | 'html' | 'presentation' | 'resource'
  duration_minutes: number
  order: number
  is_completed?: boolean
  video_url?: string
  file_url?: string
  file?: string
  content?: string
  is_free_preview?: boolean
  has_quiz?: boolean
  quiz?: { id: number } | null
  // Visione obbligatoria del video e blocco dei quiz
  require_full_view?: boolean
  video_duration_seconds?: number | null
  video_progress?: {
    position_seconds: number
    watched_seconds: number
  } | null
  is_locked?: boolean
  blocking_lessons?: { id: number; title: string; type: Lesson['type'] }[]
  blocking_module?: BlockingModule | null
  scorm?: {
    available: boolean
    version: ScormVersion | null
    status: ScormStatus
    score: number | null
  } | null
}

export type ScormVersion = '1.2' | '2004'
export type ScormStatus = 'not attempted' | 'incomplete' | 'completed' | 'passed' | 'failed'

export interface ScormLaunch {
  version: ScormVersion
  session_id: string
  launch_url: string
  values: Record<string, string>
  status: ScormStatus
  score: number | null
  is_completed: boolean
}

export interface ScormCommitBody {
  values: Record<string, string>
  finish: boolean
  session_id: string
  seq: number
}

export interface ScormCommitResult {
  status: ScormStatus
  score: number | null
  is_completed: boolean
  course_progress: number
}

export interface VideoProgress {
  position_seconds: number
  watched_seconds: number
  duration_seconds: number
  is_completed: boolean
  course_progress: number
}

export interface Enrollment {
  id: number
  course: Course
  enrolled_at: string
  progress: number
  completed_at: string | null
  final_score: number | null
  certificate_issued: boolean
}

export interface Certificate {
  id: string
  certificate_number: string
  course_title: string
  course_slug: string
  user_name: string
  issued_at: string
  completed_at: string | null
  has_pdf: boolean
}

export interface QuizAttempt {
  id: number
  quiz_id: number
  score: number
  passed: boolean
  started_at: string
  completed_at: string
  answers: {
    question_id: number
    answer_id: number
    is_correct: boolean
  }[]
}

export const lmsApi = {
  // Categories
  getCategories: async (): Promise<CourseCategory[]> => {
    const response = await apiClient.get('/lms/categories/')
    // Handle paginated response
    if (response.data.results) {
      return response.data.results
    }
    return response.data
  },

  // Courses
  getCourses: async (params?: {
    category?: string
    level?: string
    search?: string
    ordering?: string
    page?: number
  }): Promise<PaginatedResponse<Course>> => {
    const response = await apiClient.get('/lms/courses/', { params })
    return response.data
  },

  getCourse: async (slug: string): Promise<Course & { modules: Module[] }> => {
    const response = await apiClient.get(`/lms/courses/${slug}/`)
    return response.data
  },

  // Enrollments
  getMyEnrollments: async (): Promise<Enrollment[]> => {
    const response = await apiClient.get('/lms/enrollments/')
    // Handle paginated response
    if (response.data.results) {
      return response.data.results
    }
    return response.data
  },

  enroll: async (courseSlug: string): Promise<Enrollment> => {
    // Backend expects slug, not courseId
    const response = await apiClient.post(`/lms/courses/${courseSlug}/enroll/`)
    return response.data
  },

  unenroll: async (courseSlug: string): Promise<void> => {
    // Note: Backend may not have this endpoint implemented
    await apiClient.delete(`/lms/courses/${courseSlug}/enroll/`)
  },

  // Lessons
  getLesson: async (lessonId: number): Promise<Lesson> => {
    const response = await apiClient.get(`/lms/lessons/${lessonId}/`)
    return response.data
  },

  markLessonComplete: async (lessonId: number): Promise<void> => {
    await apiClient.post(`/lms/lessons/${lessonId}/complete/`)
  },

  getScormLaunch: async (lessonId: number): Promise<ScormLaunch> => {
    const response = await apiClient.get(`/lms/lessons/${lessonId}/scorm/`)
    return response.data
  },

  commitScorm: async (lessonId: number, body: ScormCommitBody): Promise<ScormCommitResult> => {
    const response = await apiClient.post(`/lms/lessons/${lessonId}/scorm/`, body)
    return response.data
  },

  updateVideoProgress: async (
    lessonId: number,
    position: number,
    duration: number
  ): Promise<VideoProgress> => {
    const response = await apiClient.post(`/lms/lessons/${lessonId}/video-progress/`, {
      position,
      duration,
    })
    return response.data
  },

  // Quizzes
  getQuiz: async (lessonId: number): Promise<{
    id: number
    title: string
    description: string
    passing_score: number
    max_attempts: number
    time_limit_minutes: number | null
    questions: {
      id: number
      text: string
      type: 'single' | 'multiple' | 'true_false'
      answers: { id: number; text: string }[]
    }[]
  }> => {
    const response = await apiClient.get(`/lms/lessons/${lessonId}/quiz/`)
    return response.data
  },

  startQuiz: async (lessonId: number): Promise<{
    attempt_id: number
    quiz_id: number
    started_at: string
  }> => {
    const response = await apiClient.post(`/lms/lessons/${lessonId}/quiz/start/`)
    return response.data
  },

  submitQuiz: async (
    attemptId: number,
    answers: Record<string, number | number[]>
  ): Promise<QuizAttempt> => {
    const response = await apiClient.post(`/lms/quiz-attempts/${attemptId}/submit/`, { answers })
    return response.data
  },

  // Certificates
  getMyCertificates: async (): Promise<Certificate[]> => {
    const response = await apiClient.get('/lms/certificates/')
    // Handle paginated response
    if (response.data.results) {
      return response.data.results
    }
    return response.data
  },

  issueCourseCertificate: async (courseSlug: string): Promise<Certificate> => {
    const response = await apiClient.post(`/lms/courses/${courseSlug}/certificate/`)
    return response.data.certificate
  },

  downloadCertificate: async (certificateId: string): Promise<Blob> => {
    const response = await apiClient.get(`/lms/certificates/${certificateId}/download/`, {
      responseType: 'blob',
    })
    return response.data
  },

  // Progress
  getCourseProgress: async (courseId: number): Promise<{
    progress: number
    completed_lessons: number
    total_lessons: number
    completed_modules: number
    total_modules: number
  }> => {
    const response = await apiClient.get(`/lms/courses/${courseId}/progress/`)
    return response.data
  },

  // Ratings
  rateCourse: async (courseId: number, rating: number, review?: string): Promise<void> => {
    await apiClient.post(`/lms/courses/${courseId}/rate/`, { rating, review })
  },

  // Admin endpoints
  admin: {
    // Categories
    getCategories: async (): Promise<AdminLmsCategory[]> => {
      const response = await apiClient.get('/lms/admin/categories/')
      // Handle paginated response
      return response.data.results || response.data || []
    },

    createCategory: async (data: CreateLmsCategoryData): Promise<AdminLmsCategory> => {
      const response = await apiClient.post('/lms/admin/categories/', data)
      return response.data
    },

    updateCategory: async (id: number, data: Partial<CreateLmsCategoryData>): Promise<AdminLmsCategory> => {
      const response = await apiClient.patch(`/lms/admin/categories/${id}/`, data)
      return response.data
    },

    deleteCategory: async (id: number): Promise<void> => {
      await apiClient.delete(`/lms/admin/categories/${id}/`)
    },

    // Courses
    getCourses: async (): Promise<AdminCourse[]> => {
      const response = await apiClient.get('/lms/admin/courses/')
      // Handle paginated response
      return response.data.results || response.data || []
    },

    createCourse: async (data: CreateCourseData): Promise<AdminCourse> => {
      const response = await apiClient.post('/lms/admin/courses/', data)
      return response.data
    },

    updateCourse: async (id: number, data: Partial<CreateCourseData>): Promise<AdminCourse> => {
      const response = await apiClient.patch(`/lms/admin/courses/${id}/`, data)
      return response.data
    },

    deleteCourse: async (id: number): Promise<void> => {
      await apiClient.delete(`/lms/admin/courses/${id}/`)
    },

    togglePublish: async (id: number): Promise<{ is_published: boolean }> => {
      const response = await apiClient.post(`/lms/admin/courses/${id}/toggle_publish/`)
      return response.data
    },

    getCourseDetail: async (id: number): Promise<AdminCourseDetail> => {
      const response = await apiClient.get(`/lms/admin/courses/${id}/full-detail/`)
      return response.data
    },

    uploadCourseThumbnail: async (id: number, file: File): Promise<{ thumbnail_url: string }> => {
      const formData = new FormData()
      formData.append('thumbnail', file)
      const response = await apiClient.post(`/lms/admin/courses/${id}/upload-thumbnail/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },

    // Modules
    getModules: async (courseId: number): Promise<AdminModule[]> => {
      const response = await apiClient.get(`/lms/admin/modules/?course=${courseId}`)
      return response.data.results || response.data || []
    },

    createModule: async (data: CreateModuleData): Promise<AdminModule> => {
      const response = await apiClient.post('/lms/admin/modules/', data)
      return response.data
    },

    updateModule: async (id: number, data: Partial<CreateModuleData>): Promise<AdminModule> => {
      const response = await apiClient.patch(`/lms/admin/modules/${id}/`, data)
      return response.data
    },

    deleteModule: async (id: number): Promise<void> => {
      await apiClient.delete(`/lms/admin/modules/${id}/`)
    },

    reorderModules: async (courseId: number, moduleIds: number[]): Promise<void> => {
      await apiClient.post('/lms/admin/modules/reorder/', { course_id: courseId, module_ids: moduleIds })
    },

    // Lessons
    getLessons: async (moduleId: number): Promise<AdminLesson[]> => {
      const response = await apiClient.get(`/lms/admin/lessons/?module=${moduleId}`)
      return response.data.results || response.data || []
    },

    getLesson: async (id: number): Promise<AdminLesson> => {
      const response = await apiClient.get(`/lms/admin/lessons/${id}/`)
      return response.data
    },

    createLesson: async (data: CreateLessonData): Promise<AdminLesson> => {
      const response = await apiClient.post('/lms/admin/lessons/', data)
      return response.data
    },

    updateLesson: async (id: number, data: Partial<CreateLessonData>): Promise<AdminLesson> => {
      const response = await apiClient.patch(`/lms/admin/lessons/${id}/`, data)
      return response.data
    },

    deleteLesson: async (id: number): Promise<void> => {
      await apiClient.delete(`/lms/admin/lessons/${id}/`)
    },

    reorderLessons: async (moduleId: number, lessonIds: number[]): Promise<void> => {
      await apiClient.post('/lms/admin/lessons/reorder/', { module_id: moduleId, lesson_ids: lessonIds })
    },

    uploadLessonFile: async (id: number, file: File): Promise<{ file_url: string; scorm: AdminLesson['scorm'] }> => {
      const formData = new FormData()
      formData.append('file', file)
      const response = await apiClient.post(`/lms/admin/lessons/${id}/upload-file/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },

    // Quizzes
    getQuizzes: async (lessonId: number): Promise<AdminQuiz[]> => {
      const response = await apiClient.get(`/lms/admin/quizzes/?lesson=${lessonId}`)
      return response.data.results || response.data || []
    },

    getQuiz: async (id: number): Promise<AdminQuiz> => {
      const response = await apiClient.get(`/lms/admin/quizzes/${id}/`)
      return response.data
    },

    createQuiz: async (data: CreateQuizData): Promise<AdminQuiz> => {
      const response = await apiClient.post('/lms/admin/quizzes/', data)
      return response.data
    },

    updateQuiz: async (id: number, data: Partial<CreateQuizData>): Promise<AdminQuiz> => {
      const response = await apiClient.patch(`/lms/admin/quizzes/${id}/`, data)
      return response.data
    },

    deleteQuiz: async (id: number): Promise<void> => {
      await apiClient.delete(`/lms/admin/quizzes/${id}/`)
    },

    getQuizAttempts: async (quizId: number): Promise<AdminQuizAttempts> => {
      const response = await apiClient.get(`/lms/admin/quizzes/${quizId}/attempts/`)
      return response.data
    },

    resetQuizAttempts: async (quizId: number, enrollmentId: number): Promise<{ reset_count: number }> => {
      const response = await apiClient.post(`/lms/admin/quizzes/${quizId}/reset-attempts/`, {
        enrollment_id: enrollmentId,
      })
      return response.data
    },

    // Questions
    getQuestions: async (quizId: number): Promise<AdminQuestion[]> => {
      const response = await apiClient.get(`/lms/admin/questions/?quiz=${quizId}`)
      return response.data.results || response.data || []
    },

    createQuestion: async (data: CreateQuestionData): Promise<AdminQuestion> => {
      const response = await apiClient.post('/lms/admin/questions/', data)
      return response.data
    },

    updateQuestion: async (id: number, data: Partial<CreateQuestionData>): Promise<AdminQuestion> => {
      const response = await apiClient.patch(`/lms/admin/questions/${id}/`, data)
      return response.data
    },

    deleteQuestion: async (id: number): Promise<void> => {
      await apiClient.delete(`/lms/admin/questions/${id}/`)
    },

    reorderQuestions: async (quizId: number, questionIds: number[]): Promise<void> => {
      await apiClient.post('/lms/admin/questions/reorder/', { quiz_id: quizId, question_ids: questionIds })
    },

    // Answers
    getAnswers: async (questionId: number): Promise<AdminAnswer[]> => {
      const response = await apiClient.get(`/lms/admin/answers/?question=${questionId}`)
      return response.data.results || response.data || []
    },

    createAnswer: async (data: CreateAnswerData): Promise<AdminAnswer> => {
      const response = await apiClient.post('/lms/admin/answers/', data)
      return response.data
    },

    updateAnswer: async (id: number, data: Partial<CreateAnswerData>): Promise<AdminAnswer> => {
      const response = await apiClient.patch(`/lms/admin/answers/${id}/`, data)
      return response.data
    },

    deleteAnswer: async (id: number): Promise<void> => {
      await apiClient.delete(`/lms/admin/answers/${id}/`)
    },

    // Certificate templates (Word)
    getCertificateTemplates: async (): Promise<CertificateTemplate[]> => {
      const response = await apiClient.get('/lms/admin/certificate-templates/')
      return response.data.results || response.data || []
    },

    getCertificatePlaceholders: async (): Promise<CertificatePlaceholder[]> => {
      const response = await apiClient.get('/lms/admin/certificate-templates/placeholders/')
      return response.data
    },

    uploadCertificateTemplate: async (data: {
      name: string
      description?: string
      file: File
      is_default?: boolean
    }): Promise<CertificateTemplate> => {
      const formData = new FormData()
      formData.append('name', data.name)
      formData.append('description', data.description || '')
      formData.append('file', data.file)
      if (data.is_default) formData.append('is_default', 'true')
      const response = await apiClient.post('/lms/admin/certificate-templates/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data
    },

    updateCertificateTemplate: async (
      id: number,
      data: { name?: string; description?: string; file?: File; is_default?: boolean }
    ): Promise<CertificateTemplate> => {
      const formData = new FormData()
      if (data.name !== undefined) formData.append('name', data.name)
      if (data.description !== undefined) formData.append('description', data.description)
      if (data.file) formData.append('file', data.file)
      if (data.is_default) formData.append('is_default', 'true')
      const response = await apiClient.patch(`/lms/admin/certificate-templates/${id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data
    },

    deleteCertificateTemplate: async (id: number): Promise<void> => {
      await apiClient.delete(`/lms/admin/certificate-templates/${id}/`)
    },

    previewCertificateTemplate: async (id: number): Promise<Blob> => {
      const response = await apiClient.get(`/lms/admin/certificate-templates/${id}/preview/`, {
        responseType: 'blob',
      })
      return response.data
    },

    downloadCertificateTemplate: async (id: number): Promise<Blob> => {
      const response = await apiClient.get(`/lms/admin/certificate-templates/${id}/download/`, {
        responseType: 'blob',
      })
      return response.data
    },

    // Reviews
    getCourseReviews: async (courseId: number): Promise<{
      count: number
      average_rating: number | null
      results: Array<{
        id: number
        user_id: number
        user_name: string
        user_email: string
        rating: number
        review: string
        rated_at: string
        completed_at: string | null
      }>
    }> => {
      const response = await apiClient.get(`/lms/admin/courses/${courseId}/reviews/`)
      return response.data
    },
  },
}

// Admin Types
export interface CertificateTemplate {
  id: number
  name: string
  description: string
  file_name: string
  is_default: boolean
  placeholders: string[]
  unknown_placeholders: string[]
  courses_count: number
  created_at: string
}

export interface CertificatePlaceholder {
  name: string
  tag: string
  description: string
  example: string
}

/** Saves a downloaded file (PDF, Word) with the given name. */
export function saveBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/** Error message from an API error, also when the response is a Blob. */
export async function apiErrorMessage(error: any, fallback: string): Promise<string> {
  const data = error?.response?.data
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).error || fallback
    } catch {
      return fallback
    }
  }
  return data?.error || data?.detail || fallback
}

export interface AdminLmsCategory {
  id: number
  name: string
  slug: string
  description: string
  icon: string
  order: number
  is_active: boolean
  courses_count: number
}

export interface CreateLmsCategoryData {
  name: string
  slug: string
  description?: string
  icon?: string
  order?: number
  is_active?: boolean
}

export interface AdminCourse {
  id: number
  title: string
  slug: string
  description: string
  short_description: string
  category: number | null
  category_name: string | null
  level: string
  duration_hours: number
  is_premium: boolean
  is_public: boolean
  is_free: boolean
  is_published: boolean
  has_certificate: boolean
  certificate_model: number | null
  sequential_modules: boolean
  modules_count: number
  lessons_count: number
  enrolled_count: number
  created_at: string
}

export interface CreateCourseData {
  title: string
  slug: string
  description?: string
  short_description?: string
  category?: number | null
  level: 'basic' | 'beginner' | 'intermediate' | 'advanced'
  duration_hours?: number
  is_premium?: boolean
  is_public?: boolean
  is_free?: boolean
  is_published?: boolean
  has_certificate?: boolean
  certificate_model?: number | null
  sequential_modules?: boolean
  min_role?: string
}

// ==================== ADMIN MODULE TYPES ====================

export interface AdminModule {
  id: number
  course: number
  title: string
  description: string
  order: number
  lessons_count: number
  created_at: string
}

export interface CreateModuleData {
  course: number
  title: string
  description?: string
  order?: number
}

// ==================== ADMIN LESSON TYPES ====================

export type LessonType = 'video' | 'pdf' | 'html' | 'presentation' | 'resource' | 'scorm' | 'quiz'

export interface AdminLesson {
  id: number
  module: number
  title: string
  type: LessonType
  content: string
  video_url: string
  file: string | null
  file_url: string | null
  duration_minutes: number
  order: number
  is_free_preview: boolean
  require_full_view: boolean
  video_duration_seconds: number | null
  scorm: {
    version: ScormVersion
    title: string
    launch: string
    sco_count: number
  } | null
  has_quiz: boolean
  created_at: string
}

export interface CreateLessonData {
  module: number
  title: string
  type: LessonType
  content?: string
  video_url?: string
  duration_minutes?: number
  order?: number
  is_free_preview?: boolean
  require_full_view?: boolean
}

// ==================== ADMIN QUIZ TYPES ====================

export interface AdminAnswer {
  id: number
  question: number
  text: string
  is_correct: boolean
  order: number
}

export interface AdminQuestion {
  id: number
  quiz: number
  text: string
  type: 'single' | 'multiple' | 'text' | 'true_false'
  points: number
  explanation: string
  order: number
  answers: AdminAnswer[]
}

export interface AdminQuiz {
  id: number
  lesson: number
  title: string
  description: string
  passing_score: number
  time_limit_minutes: number | null
  max_attempts: number
  shuffle_questions: boolean
  show_correct_answers: boolean
  questions: AdminQuestion[]
  questions_count: number
}

export interface AdminQuizUserAttempts {
  enrollment_id: number
  user_id: number
  user_name: string
  user_email: string
  attempts_used: number
  attempts_reset: number
  attempts_remaining: number
  best_score: number | null
  passed: boolean
  last_attempt_at: string | null
}

export interface AdminQuizAttempts {
  quiz_id: number
  max_attempts: number
  results: AdminQuizUserAttempts[]
}

export interface CreateQuizData {
  lesson: number
  title?: string
  description?: string
  passing_score?: number
  time_limit_minutes?: number | null
  max_attempts?: number
  shuffle_questions?: boolean
  show_correct_answers?: boolean
}

export interface CreateQuestionData {
  quiz: number
  text: string
  type: 'single' | 'multiple' | 'text' | 'true_false'
  points?: number
  explanation?: string
  order?: number
}

export interface CreateAnswerData {
  question: number
  text: string
  is_correct?: boolean
  order?: number
}

// ==================== ADMIN COURSE DETAIL TYPE ====================

export interface AdminLessonNested {
  id: number
  title: string
  type: LessonType
  duration_minutes: number
  order: number
  is_free_preview: boolean
  require_full_view: boolean
  file_url: string | null
  has_quiz: boolean
}

export interface AdminModuleNested {
  id: number
  title: string
  description: string
  order: number
  lessons: AdminLessonNested[]
  lessons_count: number
}

export interface AdminCourseDetail {
  id: number
  title: string
  slug: string
  description: string
  short_description: string
  category: number | null
  category_name: string | null
  level: string
  thumbnail: string | null
  thumbnail_url: string | null
  duration_hours: number
  is_premium: boolean
  is_public: boolean
  is_free: boolean
  is_published: boolean
  has_certificate: boolean
  certificate_model: number | null
  sequential_modules: boolean
  min_role: string
  modules: AdminModuleNested[]
  modules_count: number
  lessons_count: number
  enrolled_count: number
  created_at: string
}
